import React, { useEffect, useState } from 'react';
import {
  fetchSectionItems, fetchMemberFileUrl, toEmbedUrl, formatDate, formatSize,
} from '../../utils/membersApi';
import './Members.css';

const isImage = (f) => f.mimeType && f.mimeType.startsWith('image/');

// Loads an image through the authenticated API and shows it as a thumbnail.
const ProtectedImage = ({ itemId, file, onOpen }) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let url = null;
    let cancelled = false;
    fetchMemberFileUrl(itemId, file.id)
      .then((u) => {
        url = u;
        if (!cancelled) setSrc(u);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (url && URL.revokeObjectURL) URL.revokeObjectURL(url);
    };
  }, [itemId, file.id]);

  return (
    <button type="button" className="members-photo" onClick={() => onOpen(src)} disabled={!src}>
      {src ? <img src={src} alt={file.name} loading="lazy" /> : <span className="members-photo-loading" />}
    </button>
  );
};

const openFile = async (itemId, file) => {
  // Open a window synchronously so pop-up blockers allow it, then load the file into it.
  const win = window.open('', '_blank');
  try {
    const url = await fetchMemberFileUrl(itemId, file.id);
    if (file.mimeType === 'application/pdf' && win) {
      win.location.href = url;
      return;
    }
    if (win) win.close();
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    if (win) win.close();
    window.alert('This file could not be opened. Please log in again and retry.');
  }
};

const MemberItem = ({ item, onOpenPhoto }) => {
  const embed = toEmbedUrl(item.videoUrl);
  const photos = item.files.filter(isImage);
  const documents = item.files.filter((f) => !isImage(f));

  return (
    <article className="members-item">
      <header className="members-item-header">
        <h4>{item.title}</h4>
        <p className="members-item-meta">
          {[formatDate(item.date), item.lecturer && `Guest lecturer: ${item.lecturer}`].filter(Boolean).join(' · ')}
        </p>
      </header>

      {item.description && (
        <div className="members-item-description" dangerouslySetInnerHTML={{ __html: item.description }} />
      )}

      {embed && (
        <div className="members-video">
          <iframe
            src={embed}
            title={item.title}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}
      {!embed && item.videoUrl && (
        <a className="members-video-link" href={item.videoUrl} target="_blank" rel="noopener noreferrer">
          Watch recording →
        </a>
      )}

      {documents.length > 0 && (
        <ul className="members-files">
          {documents.map((f) => (
            <li key={f.id}>
              <button type="button" className="members-file" onClick={() => openFile(item.id, f)}>
                <span className="members-file-icon" aria-hidden="true">📄</span>
                <span className="members-file-name">{f.name}</span>
                <span className="members-file-size">{formatSize(f.size)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {photos.length > 0 && (
        <div className="members-gallery">
          {photos.map((f) => (
            <ProtectedImage key={f.id} itemId={item.id} file={f} onOpen={onOpenPhoto} />
          ))}
        </div>
      )}
    </article>
  );
};

const groupItems = (items) => {
  const groups = [];
  const byName = new Map();
  items.forEach((item) => {
    const name = item.group || '';
    if (!byName.has(name)) {
      const g = { name, items: [] };
      byName.set(name, g);
      groups.push(g);
    }
    byName.get(name).items.push(item);
  });
  return groups;
};

const MembersSection = ({ sectionId }) => {
  const [state, setState] = useState({ loading: true, items: [], error: null });
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, items: [], error: null });
    fetchSectionItems(sectionId)
      .then((items) => !cancelled && setState({ loading: false, items, error: null }))
      .catch((error) => !cancelled && setState({ loading: false, items: [], error }));
    return () => { cancelled = true; };
  }, [sectionId]);

  if (state.loading) return <p className="members-status">Loading...</p>;

  if (state.error) {
    const expired = state.error.status === 401;
    return (
      <p className="members-status members-status-error">
        {expired
          ? 'Your session has expired. Please log in again to see this content.'
          : 'This content could not be loaded right now. Please try again later.'}
      </p>
    );
  }

  if (state.items.length === 0) {
    return <p className="members-status">Nothing has been added here yet. Please check back later.</p>;
  }

  return (
    <div className="members-section-content">
      {groupItems(state.items).map((group) => (
        <section key={group.name || 'general'} className="members-group">
          {group.name && <h3 className="members-group-title">{group.name}</h3>}
          {group.items.map((item) => (
            <MemberItem key={item.id} item={item} onOpenPhoto={setLightbox} />
          ))}
        </section>
      ))}

      {lightbox && (
        <div className="members-lightbox" role="dialog" aria-label="Photo" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" />
          <button type="button" className="members-lightbox-close" aria-label="Close">×</button>
        </div>
      )}
    </div>
  );
};

export default MembersSection;
